/**
 * Admin > Nodes: the other Parallax nodes this server hears from, the Netbird
 * routes to their camera subnets, and the CHAOS bridge.
 *
 * Everything an admin can change here is a decision about trust between
 * systems: cutting a node off, routing traffic through one, pointing at an
 * arbitrator. Each is a button with its consequence written beside it, and
 * none of them reaches an aircraft.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import type { ApiResult } from '@/api/client';
import {
  addNetbirdRoute,
  disconnectChaos,
  getChaosStatus,
  getFederationStatus,
  getNetbirdRoutes,
  getNetbirdStatus,
  getNodes,
  getRemoteCameras,
  reconnectChaos,
  removeNetbirdRoute,
  restoreNode,
  revokeNode,
  type ChaosStatus,
  type FederationNode,
  type FederationStatus,
  type NetbirdRoute,
  type NodeState,
  type RemoteCamera,
} from '@/api/federation';

const MUTED = 'rgba(244,244,245,0.55)';
const OK = 'oklch(0.78 0.15 155)';
const CAUTION = 'oklch(0.8 0.15 80)';
const DANGER_FG = 'oklch(0.72 0.19 28)';
const POLL_MS = 5000;

const STATE_COLOR: Record<NodeState, string> = {
  live: OK,
  stale: CAUTION,
  never: MUTED,
  revoked: DANGER_FG,
};

export function since(ts: number | null | undefined): string {
  if (!ts) return 'never';
  const s = Math.max(0, Date.now() / 1000 - ts);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5 border-b px-6 py-5" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
      <div className="flex items-center gap-3">
        <h2 className="text-[13px] font-semibold tracking-wide">{title}</h2>
        <div className="ml-auto text-[11px]" style={{ color: MUTED }}>
          {aside}
        </div>
      </div>
      {children}
    </section>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p className="text-[11.5px] leading-relaxed" style={{ color: MUTED }}>
      {children}
    </p>
  );
}

function Pill({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span
      className="rounded-[99px] border px-2 py-0.5 font-mono text-[10px] uppercase"
      style={{ color, borderColor: `color-mix(in oklch, ${color} 45%, transparent)` }}
    >
      {children}
    </span>
  );
}

function ActionButton({
  onClick,
  busy,
  danger,
  children,
  title,
}: {
  onClick: () => void;
  busy?: boolean;
  danger?: boolean;
  children: ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title={title}
      className="rounded-[99px] border px-3 py-1 text-[11px] font-semibold disabled:opacity-40"
      style={{
        borderColor: danger ? DANGER_FG : 'rgba(255,255,255,0.18)',
        color: danger ? DANGER_FG : '#f4f4f5',
      }}
    >
      {children}
    </button>
  );
}

function ThisNode({ status }: { status: FederationStatus | null }) {
  if (!status) return <Note>Reading this node's federation settings…</Note>;
  const edge = status.edge;
  return (
    <div className="flex flex-col gap-1.5 text-[12px]">
      <div>
        This server is node <span className="font-mono">{status.nodeId}</span>. A node it hears from
        is marked stale after {Math.round(status.staleAfterS)} s of silence and dropped after{' '}
        {Math.round(status.expireAfterS / 60)} min.
      </div>
      {edge.configured ? (
        <div className="flex flex-wrap items-center gap-2">
          <span>
            Pushing to <span className="font-mono">{edge.cloudUrl}</span> every {edge.intervalS} s.
          </span>
          {edge.status.consecutiveFailures === 0 && edge.status.lastSuccessAt ? (
            <Pill color={OK}>delivered {since(edge.status.lastSuccessAt)}</Pill>
          ) : edge.status.attempts === 0 ? (
            <Pill color={MUTED}>starting</Pill>
          ) : (
            <Pill color={DANGER_FG}>
              failing: {edge.status.lastError || `HTTP ${edge.status.lastStatus ?? '?'}`}
            </Pill>
          )}
        </div>
      ) : (
        <Note>
          This server does not push its own picture anywhere. Set FEDERATION_CLOUD_URL and
          FEDERATION_PUSH_TOKEN to make it an edge.
        </Note>
      )}
      <div style={{ color: MUTED }}>
        ISR catalog: {status.isr.catalog} emitters, {status.isr.sightings} sampled sightings
        {status.isr.evicted ? `, ${status.isr.evicted} evicted at the ceiling` : ''}.
      </div>
    </div>
  );
}

function NodeRow({
  node,
  netbirdOn,
  routes,
  reload,
}: {
  node: FederationNode;
  netbirdOn: boolean;
  routes: NetbirdRoute[];
  reload: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<ApiResult<unknown>>) => {
    setBusy(true);
    setError(null);
    const result = await action();
    if (!result.ok) setError(result.error.message);
    setBusy(false);
    await reload();
  };

  const revoke = () => {
    if (
      !window.confirm(
        `Revoke ${node.nodeId}? Its pushes are refused from now on, its aircraft leave the map at once, and any Netbird route through it is removed. You can restore it later.`,
      )
    ) {
      return;
    }
    void run(() => revokeNode(node.nodeId));
  };

  const routed = new Set(routes.filter((r) => r.nodeId === node.nodeId).map((r) => r.network));

  return (
    <div className="rounded-[14px] border px-4 py-3" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-[13px]">{node.nodeId}</span>
        <Pill color={STATE_COLOR[node.state]}>{node.state}</Pill>
        <span className="text-[11px]" style={{ color: MUTED }}>
          last report {since(node.lastSeen)} · {node.reports} reports · {node.aircraft} aircraft ·{' '}
          {node.cameras} cameras
        </span>
        <div className="ml-auto">
          {node.revoked ? (
            <ActionButton busy={busy} onClick={() => void run(() => restoreNode(node.nodeId))}>
              Restore
            </ActionButton>
          ) : (
            <ActionButton busy={busy} danger onClick={revoke}>
              Revoke
            </ActionButton>
          )}
        </div>
      </div>
      {node.revoked ? (
        <div className="mt-1 text-[11px]" style={{ color: MUTED }}>
          Revoked by {node.revokedBy ?? 'an admin'} {since(node.revokedAt)}.
        </div>
      ) : null}
      {node.subnets.length ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px]">
          <span style={{ color: MUTED }}>Camera subnets</span>
          {node.subnets.map((subnet) => (
            <span key={subnet} className="flex items-center gap-1.5">
              <span className="font-mono">{subnet}</span>
              {routed.has(subnet) ? (
                <Pill color={OK}>routed</Pill>
              ) : netbirdOn && node.netbirdIp && !node.revoked ? (
                <ActionButton
                  busy={busy}
                  title={`Route ${subnet} through ${node.nodeId}'s Netbird peer ${node.netbirdIp}`}
                  onClick={() => void run(() => addNetbirdRoute(node.nodeId, subnet))}
                >
                  Route via Netbird
                </ActionButton>
              ) : null}
            </span>
          ))}
          {node.netbirdIp ? (
            <span style={{ color: MUTED }}>
              peer <span className="font-mono">{node.netbirdIp}</span>
            </span>
          ) : (
            <span style={{ color: MUTED }}>no Netbird address reported</span>
          )}
        </div>
      ) : null}
      {node.lastResult.dropped && Object.keys(node.lastResult.dropped).length ? (
        <div className="mt-1 text-[11px]" style={{ color: CAUTION }}>
          Last report dropped{' '}
          {Object.entries(node.lastResult.dropped)
            .map(([kind, n]) => `${n} ${kind}`)
            .join(', ')}
          . The node's own log says which and why.
        </div>
      ) : null}
      {error ? (
        <div className="mt-1 text-[11px]" style={{ color: DANGER_FG }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

function Cameras({ cameras }: { cameras: RemoteCamera[] }) {
  if (cameras.length === 0) {
    return (
      <Note>
        No camera addresses from other nodes. An edge publishes the feeds configured for its aircraft
        when they sit inside the subnets it declares in FEDERATION_EDGE_SUBNETS.
      </Note>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      {cameras.map((camera) => (
        <div key={camera.key} className="flex flex-wrap items-center gap-2 text-[12px]">
          <span className="font-mono">{camera.key}</span>
          <span style={{ color: MUTED }}>{camera.label}</span>
          <span className="min-w-0 truncate font-mono text-[11px]" title={camera.url}>{camera.url}</span>
          {camera.needsLogin ? <Pill color={CAUTION}>needs login</Pill> : null}
          {camera.playable === 'needs-gateway' ? (
            <Pill color={MUTED}>needs a video gateway</Pill>
          ) : null}
          {camera.freshness === 'stale' ? <Pill color={CAUTION}>stale</Pill> : null}
        </div>
      ))}
      <Note>
        Addresses only: no video passes through this server. Reaching one needs a route to the edge's
        subnet (below), and an RTSP feed also needs a video gateway to repackage it for a browser. The
        camera's login never leaves the edge; whoever wires a gateway to it supplies it there.
      </Note>
    </div>
  );
}

function Netbird({
  configured,
  host,
  routes,
  error,
  reload,
}: {
  configured: boolean;
  host?: string;
  routes: NetbirdRoute[];
  error: string | null;
  reload: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  if (!configured) {
    return (
      <Note>
        Netbird route management is off. It needs NETBIRD_API_URL (https), NETBIRD_API_TOKEN and
        NETBIRD_ROUTE_GROUP in this server's environment. The token is never shown here.
      </Note>
    );
  }
  const remove = async (route: NetbirdRoute) => {
    if (!window.confirm(`Remove the route to ${route.network}? The cloud loses its path to that subnet.`)) {
      return;
    }
    setBusy(route.routeId);
    await removeNetbirdRoute(route.routeId);
    setBusy(null);
    await reload();
  };
  return (
    <div className="flex flex-col gap-1.5 text-[12px]">
      <div style={{ color: MUTED }}>
        Managing routes on <span className="font-mono">{host}</span>. Only routes this server made are
        listed; anything else in the Netbird account is left alone.
      </div>
      {error ? <div style={{ color: DANGER_FG }}>{error}</div> : null}
      {routes.length === 0 && !error ? <Note>No routes yet. Add one from a node's camera subnet above.</Note> : null}
      {routes.map((route) => (
        <div key={route.routeId} className="flex items-center gap-2">
          <span className="font-mono">{route.network}</span>
          <span style={{ color: MUTED }}>via {route.nodeId ?? 'unknown node'}</span>
          {route.enabled ? <Pill color={OK}>enabled</Pill> : <Pill color={MUTED}>disabled</Pill>}
          <div className="ml-auto">
            <ActionButton danger busy={busy === route.routeId} onClick={() => void remove(route)}>
              Remove
            </ActionButton>
          </div>
        </div>
      ))}
    </div>
  );
}

function Chaos({ status, reload }: { status: ChaosStatus | null; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  if (!status) return <Note>Reading the bridge status…</Note>;
  if (!status.running && status.problems.length === 0 && !status.enabled) {
    return (
      <Note>
        Off. Set CHAOS_ENABLED=1 with CHAOS_ARBITRATOR_HOST and CHAOS_PASSWORD, and install protobuf
        and the arbitrator's arbitrator_api package on the server, to turn it on.
      </Note>
    );
  }
  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    await action();
    setBusy(false);
    await reload();
  };
  return (
    <div className="flex flex-col gap-2 text-[12px]">
      {status.problems.length ? (
        <div className="flex flex-col gap-1">
          <div style={{ color: CAUTION }}>Enabled, but not started:</div>
          {status.problems.map((problem) => (
            <div key={problem} className="font-mono text-[11px]">
              {problem}
            </div>
          ))}
        </div>
      ) : null}
      {status.config ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono">
            {status.config.useTls ? 'wss' : 'ws'}://{status.config.host}:{status.config.port}
          </span>
          <span style={{ color: MUTED }}>as {status.config.username}</span>
          <Pill color={status.connected ? OK : status.running ? CAUTION : MUTED}>{status.state}</Pill>
          {!status.config.useTls ? <Pill color={DANGER_FG}>unencrypted</Pill> : null}
          <div className="ml-auto flex gap-2">
            <ActionButton busy={busy} onClick={() => void act(reconnectChaos)}>
              Reconnect
            </ActionButton>
            <ActionButton busy={busy} danger onClick={() => void act(disconnectChaos)}>
              Disconnect
            </ActionButton>
          </div>
        </div>
      ) : null}
      {status.lastError ? <div style={{ color: DANGER_FG }}>{status.lastError}</div> : null}
      {status.stats ? (
        <div style={{ color: MUTED }}>
          {status.stats.messagesReceived} messages received · {status.stats.commandsRefused} orders
          from the arbitrator refused · {status.stats.commandsExecuted} operator commands sent
        </div>
      ) : null}
      <Note>
        Orders the arbitrator sends are refused and counted, never passed to an aircraft. Pointing the
        bridge at a different arbitrator, user or TLS setting needs that destination's password.
      </Note>
    </div>
  );
}

export function NodesTab(): ReactNode {
  const [status, setStatus] = useState<FederationStatus | null>(null);
  const [nodes, setNodes] = useState<FederationNode[]>([]);
  const [cameras, setCameras] = useState<RemoteCamera[]>([]);
  const [netbird, setNetbird] = useState<{ configured: boolean; host?: string }>({ configured: false });
  const [routes, setRoutes] = useState<NetbirdRoute[]>([]);
  const [routeError, setRouteError] = useState<string | null>(null);
  const [chaos, setChaos] = useState<ChaosStatus | null>(null);

  const reload = useCallback(async () => {
    const [s, n, c, nb, ch] = await Promise.all([
      getFederationStatus(),
      getNodes(),
      getRemoteCameras(),
      getNetbirdStatus(),
      getChaosStatus(),
    ]);
    if (s.ok) setStatus(s.data);
    if (n.ok) setNodes(n.data.nodes);
    if (c.ok) setCameras(c.data.cameras);
    if (ch.ok) setChaos(ch.data);
    const configured = nb.ok && nb.data.configured;
    setNetbird(nb.ok ? nb.data : { configured: false });
    if (configured) {
      const r = await getNetbirdRoutes();
      setRoutes(r.ok ? r.data.routes : []);
      setRouteError(r.ok ? null : r.error.message);
    } else {
      setRoutes([]);
    }
  }, []);

  useEffect(() => {
    void reload();
    const timer = setInterval(() => void reload(), POLL_MS);
    return () => clearInterval(timer);
  }, [reload]);

  return (
    <div className="flex flex-col">
      <Section title="This node">
        <ThisNode status={status} />
      </Section>
      <Section title="Other nodes" aside={`${nodes.length} known`}>
        {nodes.length === 0 ? (
          <Note>
            No node has reported yet. A node can push here once this server holds a secret for it in
            FEDERATION_TOKEN_&lt;NODE&gt;, for example FEDERATION_TOKEN_EDGE_1 for node edge-1. Its
            aircraft show on Command for admins and observers, never commandable.
          </Note>
        ) : (
          <div className="flex flex-col gap-2">
            {nodes.map((node) => (
              <NodeRow
                key={node.nodeId}
                node={node}
                netbirdOn={netbird.configured}
                routes={routes}
                reload={reload}
              />
            ))}
          </div>
        )}
      </Section>
      <Section title="Remote cameras" aside={`${cameras.length} published`}>
        <Cameras cameras={cameras} />
      </Section>
      <Section title="Netbird routes">
        <Netbird
          configured={netbird.configured}
          host={netbird.host}
          routes={routes}
          error={routeError}
          reload={reload}
        />
      </Section>
      <Section title="CHAOS arbitrator">
        <Chaos status={chaos} reload={reload} />
      </Section>
    </div>
  );
}

export default NodesTab;
